import Phaser from 'phaser';
import { VIEWPORT_W } from '../../constants';
import { onGame, offGame, GameEvents } from '../../events';
import { isBackendEnabled } from '../../../net/backend';
import {
  breakDustParticleCount,
  clearBreakLootSnapshot,
} from '../../server-state';
import type { GeodeModel } from '../types';
import { createGeodeController } from './geode-controller';
import { createGeodeView } from './geode-view';
import {
  createGeodeDrops,
  OFFLINE_BREAK_DUST_MIN,
  OFFLINE_BREAK_DUST_RANGE,
} from './geode-drops';
import { createSmashParticles } from './smash-particles';
import { geodeLayoutFromModel } from './geode-model';

export function createGeodeComponent(scene: Phaser.Scene, model: GeodeModel) {
  let currentModel = model;
  const getModel = () => currentModel;

  const smashParticles = createSmashParticles(scene);
  const drops = createGeodeDrops(scene);

  let controller!: ReturnType<typeof createGeodeController>;
  const state = {
    get controller() {
      return controller;
    },
  };

  const view = createGeodeView(
    scene,
    getModel,
    {
      isBroken: () => state.controller?.isBroken() ?? false,
      isRespawning: () => state.controller?.isRespawning() ?? false,
    },
    () => state.controller?.respawn(),
    () => {
      controller.finishRespawn();
      controller.syncVisual();
    },
  );

  controller = createGeodeController(
    scene,
    getModel,
    view.sprite,
    {
      onCrackFrame: view.applyCrackFrame,
      onHitReaction: view.onHitReaction,
      onHitParticles: (x, y, opts) => smashParticles.spawnAt(x, y, opts),
      onBreak: () => {
        void view.playBreakSequence();
      },
      onRespawnStart: view.onRespawnStart,
      startRespawnTween: view.startRespawnTween,
      onRespawnComplete: () => {
        controller.syncVisual();
      },
      isVisible: view.isVisible,
      isRespawning: () => controller.isRespawning(),
    },
    (pointer) => drops.pointerAtDrop(pointer),
  );

  const onGeodeBreak = () => {
    const { cx, cy } = geodeLayoutFromModel(currentModel);
    const count = isBackendEnabled()
      ? breakDustParticleCount()
      : OFFLINE_BREAK_DUST_MIN + Math.floor(Math.random() * OFFLINE_BREAK_DUST_RANGE);
    if (count <= 0) return;
    drops.spawnDustBurst(cx, cy, {
      count,
      fromServerGrant: isBackendEnabled(),
    });
    clearBreakLootSnapshot();
  };

  const onServerDrops = (payload: unknown) => {
    const { items = [] } = (payload ?? {}) as { items?: string[] };
    if (!items.length) return;
    const { cx, cy } = geodeLayoutFromModel(currentModel);
    drops.spawnItems(cx, cy, items);
  };

  onGame(GameEvents.GEODE_BREAK, onGeodeBreak);
  onGame(GameEvents.SERVER_DROPS, onServerDrops);

  function setModel(next: GeodeModel) {
    currentModel = next;
    view.setModel();
    controller.syncVisual();
  }

  function syncFromScroll(scrollX: number) {
    view.setPanelVisible(scrollX > VIEWPORT_W * 0.5);
  }

  return {
    onTap: controller.onTap,
    pointerAtDrop: drops.pointerAtDrop,
    tryPickup: drops.tryPickup,
    setModel,
    syncFromScroll,
    updateParticles: smashParticles.update,
    destroy() {
      offGame(GameEvents.GEODE_BREAK, onGeodeBreak);
      offGame(GameEvents.SERVER_DROPS, onServerDrops);
      controller.destroy();
      view.destroy();
      drops.clear();
    },
  };
}

export { queueGeodeLoad, prepareGeode } from './geode-load';
export type { GeodeModel } from './geode-model';
